"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { ShieldCheck, Cpu, SlidersHorizontal, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface OverviewCopy {
  badge: string;
  verifiedLabel: string;
  heading: string;
  lead: string;
  point1Title: string;
  point1Desc: string;
  point2Title: string;
  point2Desc: string;
  point3Title: string;
  point3Desc: string;
  ctaTechData: string;
  imgAlt: string;
}
const copyData: Record<Lang, OverviewCopy> = {
  en: {
    badge: "Continuous-Slot Precision",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Engineered Profile-Wire Assemblies for High-Load Mechanical Separation",
    lead: "From rotary trommels and high-collapse intake well candles to heavy vibratory dewatering panels and resin underdrain nozzles, MutualFilter manufactures all-welded wedge wire screens with continuous non-clogging slot geometry.",
    point1Title: "Automated Resistance Fusion Welding",
    point1Desc: "Simultaneous current and high pressure fuse cold-rolled V-wire to every transverse support rod, delivering extreme differential burst and collapse resistance without filler metals.",
    point2Title: "Non-Blinding Continuous Slot Geometry",
    point2Desc: "The outward-narrowing V-profile provides clean two-point particle contact at the face, allowing near-size solids to clear immediately without mid-depth wedging or clogging.",
    point3Title: "Precision Alloy Metallurgy & Sizing",
    point3Desc: "Manufactured from AISI 304, 316L, and Duplex 2205 with aperture ratings from 20 µm to 5.0 mm for demanding water, mining, chemical, and food processes.",
    ctaTechData: "Selection Guide",
    imgAlt: "Continuous slot wedge wire screen cylindrical assembly and resistance welded elements"
  },
  zh: {
    badge: "连续缝隙精密制造",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "专为高负荷机械分离设计的工程级型线焊接滤网",
    lead: "从旋转滚筒筛与高抗压滤水管，到重型振动脱水筛板与树脂截留滤水帽，MutualFilter 制造具备连续自清理 V 型缝隙的全焊接约翰逊楔形网组件。",
    point1Title: "全自动电阻熔焊结构",
    point1Desc: "在冷轧 V 型丝与纵向支撑杆每个交叉点同步施加精密电流与压力，实现无填充金属的一体化冶金结合，具备极高抗压溃与抗爆破强度。",
    point2Title: "连续自清理双点接触防堵缝隙",
    point2Desc: "外窄内宽梯形剖面在筛面形成精密双点接触线，通过缝隙的微粒立即随流体释出，彻底消除深层颗粒卡堵与网孔盲死。",
    point3Title: "精密合金冶金与缝隙公差",
    point3Desc: "采用 304、316L 及 2205 双相不锈钢精工制造，缝隙精度范围覆盖 20 µm 至 5.0 mm，满足水处理、矿业脱水、化工及食品医药严苛工况。",
    ctaTechData: "选型指南",
    imgAlt: "精密不锈钢连续缝隙楔形丝滤筒组件与电阻熔焊结构"
  },
  es: {
    badge: "Precisión de ranura continua",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Conjuntos de alambre perfilado para separación mecánica de alta carga",
    lead: "Desde tambores rotativos y bujías filtrantes de alto colapso hasta paneles vibratorios de deshidratación y toberas de retención de resina, fabricamos rejillas electrosoldadas continuas antiobstrucción.",
    point1Title: "Soldadura por resistencia continua",
    point1Desc: "Fusión por corriente y presión en cada intersección entre alambre en V y varillas transversales, garantizando máxima rigidez sin metales de aporte.",
    point2Title: "Geometría anticolmatado de ranura continua",
    point2Desc: "El perfil en V crea contacto en solo dos puntos en la cara exterior, permitiendo el paso limpio de finos sin aprisionamiento.",
    point3Title: "Metalurgia precisa y tolerancias estrictas",
    point3Desc: "Fabricación en AISI 304, 316L y Dúplex 2205 con aberturas de 20 µm a 5.0 mm para aplicaciones de agua, minería y química.",
    ctaTechData: "Guía de selección",
    imgAlt: "Cilindro de alambre de cuña electrosoldado de ranura continua"
  },
  de: {
    badge: "Präzisions-Endlosspalt",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Spaltsiebbaugruppen für anspruchsvolle mechanische Trennprozesse",
    lead: "Von rotierenden Trommelsieben und kollapsfesten Brunnenfilterkerzen bis hin zu Schwingsiebböden und Filterdüsen fertigen wir vollverschweißte Spaltsiebe mit selbstreinigendem Endlosspalt.",
    point1Title: "Automatisierte Widerstandsschweißung",
    point1Desc: "Druck- und stromverschweißte Kreuzungspunkte zwischen V-Draht und Tragstäben sorgen für maximale Berst- und Kollapsfestigkeit ohne Zusatzwerkstoffe.",
    point2Title: "Selbstreinigende Spaltgeometrie",
    point2Desc: "Das sich nach innen erweiternde V-Profil sorgt für Zweipunktkontakt und verhindert Verkeilen und Zusetzen wirksam.",
    point3Title: "Werkstoffvielfalt & enge Spaltweiten",
    point3Desc: "Gefertigt aus 1.4301 (304), 1.4404 (316L) und Duplex 2205 mit Spaltweiten von 20 µm bis 5,0 mm für Wasseraufbereitung, Bergbau und Chemie.",
    ctaTechData: "Auswahlleitfaden",
    imgAlt: "Präzisionsgeschweißtes Spaltsiebrohr und Zylinderelemente"
  },
  fr: {
    badge: "Précision de fente continue",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Ensembles en fil profilé pour la séparation mécanique à fortes charges",
    lead: "Des tambours rotatifs et bougies filtrantes haute résistance aux grilles d'égouttage vibrantes et crépines de rétention de résine, nous fabriquons des grilles soudées à fente continue anticolmatage.",
    point1Title: "Soudure par résistance automatisée",
    point1Desc: "Fusion simultanée à chaque croisement entre fil en V et barres supports assurant une tenue exceptionnelle à l'écrasement sans métal d'apport.",
    point2Title: "Fente continue autonettoyante",
    point2Desc: "Le profil évasé vers l'aval assure un contact particulaire en deux points, éliminant tout risque de coincement ou de colmatage.",
    point3Title: "Métallurgie de précision et tolérances strictes",
    point3Desc: "Réalisées en inox 304, 316L et duplex 2205 avec ouvertures de 20 µm à 5,0 mm pour les procédés de traitement d'eau, miniers et chimiques.",
    ctaTechData: "Guide de sélection",
    imgAlt: "Ensemble cylindrique en fil de coin soudé par résistance"
  },
  pt: {
    badge: "Precisão de fenda contínua",
    verifiedLabel: "ISO 9044 / ASTM E2016",
    heading: "Conjuntos de arame perfilado para separação mecânica sob alta carga",
    lead: "De tambores rotativos e velas filtrantes resistentes ao colapso a telas vibratórias de desaguamento e crepinas para leito de resina, fabricamos telas eletrossoldadas com abertura contínua antientupimento.",
    point1Title: "Soldagem por resistência automatizada",
    point1Desc: "Fusão simultânea em cada interseção entre o arame em V e as barras de suporte, conferindo resistência máxima à pressão diferencial sem metal de adição.",
    point2Title: "Geometria antientupimento de fenda contínua",
    point2Desc: "O perfil em V com alargamento interno garante contato em apenas dois pontos na superfície, liberando partículas sem colmatação.",
    point3Title: "Metalurgia precisa e faixas de abertura",
    point3Desc: "Produzidas em AISI 304, 316L e Duplex 2205 com aberturas de 20 µm a 5,0 mm para tratamento de água, mineração, química e alimentos.",
    ctaTechData: "Guia de seleção",
    imgAlt: "Conjunto cilíndrico de tela de arame cunha eletrossoldada"
  }
};
export default function Overview() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, copyData);
  const keyPoints = [{
    icon: Cpu,
    title: c.point1Title,
    desc: c.point1Desc
  }, {
    icon: SlidersHorizontal,
    title: c.point2Title,
    desc: c.point2Desc
  }, {
    icon: ShieldCheck,
    title: c.point3Title,
    desc: c.point3Desc
  }];
  return <section id="wedge-wire-overview" data-nav="light" className="bg-background text-foreground border-b border-border mf-section">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left Column: Large Framed Industrial Photo (no overlay, subtle zoom) */}
          <div className="lg:col-span-6">
            <Reveal className="w-full">
              <div className="mf-media-framed mf-ratio-card w-full mf-img-zoom">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/wedge_wire_screen_manufacturing.webp-wvc-sizes" alt={c.imgAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              </div>
            </Reveal>
          </div>

          {/* Right Column: Lead + Exactly Three Key Points */}
          <Reveal delay={80} className="lg:col-span-6 flex flex-col justify-center">
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-sm tracking-wide bg-card text-foreground">
                {c.badge}
              </Badge>
              <span className="text-sm font-mono text-muted-foreground">
                {c.verifiedLabel}
              </span>
            </div>

            <h2 className="font-default tracking-tight text-foreground mf-h2">
              {c.heading}
            </h2>

            <p className="mt-4 text-muted-foreground mf-lead max-w-[65ch]">
              {c.lead}
            </p>

            {/* Exactly Three Key Points */}
            <div className="mt-6 flex flex-col gap-3.5">
              {keyPoints.map((point, index) => {
              const IconComponent = point.icon;
              return <Reveal key={point.title} delay={120 + index * 80}>
                    <div className="flex items-start gap-3.5 p-3.5 rounded-lg bg-card border border-border">
                      <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                        <IconComponent className="size-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="font-default font-semibold text-base text-foreground leading-snug">
                          {point.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">
                          {point.desc}
                        </p>
                      </div>
                    </div>
                  </Reveal>;
            })}
            </div>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-6 border-border bg-card hover:bg-muted font-semibold">
                <Link to="/WedgeWireScreenSelectionGuidePage">
                  {c.ctaTechData}
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}